import { useState } from 'react';
import type { CurrencyCode } from '@/shared/lib/format';
import { AlertTriangle, Minus, Plus, ScanLine } from 'lucide-react';
import { Label } from '@/shared/ui/label';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Switch } from '@/shared/ui/switch';
import { quantityLabel } from '@/shared/lib/passbook-utils';
import type { Passbook } from '@/shared/types';
import type { Product } from '@/shared/types/product';
import {
  isStampsDiscountRedemption,
  usesManualStampCount,
  usesProductDetailsInput,
  usesPurchaseValueInput,
} from '../model/operator-rules';
import { purchaseCodeFieldMeta, purchaseValueFieldMeta } from '../model/operator-add-fields';
import { sanitizePurchaseCode } from '../model/operator-external-id';
import type { OperatorSaleDetail } from '../model/operator-payload';
import { OperatorMoneyField } from './operator-money-field';
import { OperatorAddFields } from './operator-add-fields';
import { OperatorProductPicker, OperatorSaleLines } from './operator-product-picker';

interface OperatorAccountStepProps {
  passbook: Passbook;
  purchaseValue: string;
  onPurchaseValueChange: (v: string) => void;
  /** Conta só-resgate: não exige valor; APIs usam R$ 0,01. */
  redemptionOnly?: boolean;
  onRedemptionOnlyChange?: (on: boolean) => void;
  /** resgateNaoPontua+role sem crédito: valor da compra é obrigatório, esconde o toggle. */
  redemptionOnlyDisabled?: boolean;
  purchaseCode: string;
  onPurchaseCodeChange: (v: string) => void;
  addFieldValues: Record<string, string>;
  onAddFieldChange: (slug: string, value: string) => void;
  addFieldErrors?: Record<string, string>;
  currencyCode: CurrencyCode;
  stampCount: number;
  onStampCountChange: (n: number) => void;
  nfceKey?: string;
  onScanNfce: () => void;
  storeProducts?: Product[];
  storeProductsLoading?: boolean;
  saleDetails?: OperatorSaleDetail[];
  onSaleDetailChange?: (sku: string, name: string, count: number) => void;
  forceShowProducts?: boolean;
}

export function OperatorAccountStep({
  passbook,
  purchaseValue,
  onPurchaseValueChange,
  redemptionOnly = false,
  onRedemptionOnlyChange,
  redemptionOnlyDisabled = false,
  purchaseCode,
  onPurchaseCodeChange,
  addFieldValues,
  onAddFieldChange,
  addFieldErrors,
  currencyCode,
  stampCount,
  onStampCountChange,
  nfceKey,
  onScanNfce,
  storeProducts = [],
  storeProductsLoading = false,
  saleDetails = [],
  onSaleDetailChange,
  forceShowProducts = false,
}: OperatorAccountStepProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const label = quantityLabel(passbook);
  const showPurchase = usesPurchaseValueInput(passbook);
  const showRedemptionToggle =
    showPurchase && !isStampsDiscountRedemption(passbook) && !redemptionOnlyDisabled;
  const showStampCounter = usesManualStampCount(passbook) && !redemptionOnly;
  const showProducts = (usesProductDetailsInput(passbook) || forceShowProducts) && !redemptionOnly;
  const purchaseCodeMeta = purchaseCodeFieldMeta(passbook);
  const purchaseValueMeta = purchaseValueFieldMeta(passbook);
  const purchaseCodeError = addFieldErrors?.numberCodCupom;

  const handleSaleDetailChange = (sku: string, name: string, count: number) => {
    onSaleDetailChange?.(sku, name, count);
  };

  return (
    <div className="space-y-4 sm:space-y-5">
      <div className="flex shrink-0 items-end justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
            Conta
          </p>
          <h3 className="text-lg font-semibold tracking-tight">
            {redemptionOnly
              ? 'Só resgate'
              : showProducts
                ? 'Produtos e valor da compra'
                : 'Valor da compra'}
          </h3>
        </div>
        {showPurchase && !redemptionOnly ? (
          <Button
            disabled
            type="button"
            variant="ghost"
            size="sm"
            className="h-10 gap-1.5"
            onClick={onScanNfce}
          >
            <ScanLine className="size-4" />
            Nota fiscal
          </Button>
        ) : null}
      </div>

      {showRedemptionToggle && onRedemptionOnlyChange ? (
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-[hsl(var(--glass-border)/0.45)] px-3 py-3">
          <div className="min-w-0">
            <p className="text-sm font-medium">Operação apenas de resgate</p>
            <p className="text-xs text-muted-foreground">
              Sem valor de compra — só aplica benefício no próximo passo.
            </p>
          </div>
          <Switch
            checked={redemptionOnly}
            onCheckedChange={onRedemptionOnlyChange}
            aria-label="Conta só resgate"
          />
        </div>
      ) : null}

      {showPurchase && !redemptionOnly ? (
        <div className="space-y-1.5">
          <OperatorMoneyField
            id="purchase-value"
            label={
              purchaseValueMeta.required ? `${purchaseValueMeta.label} *` : purchaseValueMeta.label
            }
            value={purchaseValue}
            onChange={onPurchaseValueChange}
            currencyCode={currencyCode}
            autoFocus={!showProducts}
            hint={nfceKey ? `NFC-e: ${nfceKey}` : undefined}
          />
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-400">
            <AlertTriangle className="size-3" />
            (Sem desconto)
          </span>
        </div>
      ) : null}

      <div className="space-y-1.5">
        <Label htmlFor="purchase-code" className="text-sm">
          {purchaseCodeMeta.label}
          {purchaseCodeMeta.required ? <span className="text-destructive"> *</span> : null}
        </Label>
        <Input
          id="purchase-code"
          value={purchaseCode}
          onChange={(e) => onPurchaseCodeChange(sanitizePurchaseCode(e.target.value))}
          placeholder="Gerado automaticamente se vazio"
          className={`h-11 rounded-2xl ${purchaseCodeError ? 'border-destructive focus-visible:ring-destructive/40' : ''}`}
          autoComplete="off"
          aria-invalid={Boolean(purchaseCodeError)}
        />
        {purchaseCodeError ? (
          <p className="text-sm font-medium text-destructive">{purchaseCodeError}</p>
        ) : (
          <p className="text-xs text-muted-foreground">
            Use quando tiver o código do cupom. Sem código, o sistema gera um identificador interno.
          </p>
        )}
      </div>

      {showProducts ? (
        <>
          <OperatorSaleLines
            saleDetails={saleDetails}
            onSaleDetailChange={handleSaleDetailChange}
            onAddClick={() => setPickerOpen(true)}
            loading={storeProductsLoading}
            emptyCatalog={!storeProductsLoading && storeProducts.length === 0}
          />
          <OperatorProductPicker
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            products={storeProducts}
            loading={storeProductsLoading}
            saleDetails={saleDetails}
            onSaleDetailChange={handleSaleDetailChange}
          />
        </>
      ) : null}

      <OperatorAddFields
        passbook={passbook}
        values={addFieldValues}
        onChange={onAddFieldChange}
        errors={addFieldErrors}
      />

      {showStampCounter ? (
        <div className="space-y-2">
          <Label>{label}</Label>
          <div className="flex items-center justify-center gap-3">
            <Button
              type="button"
              variant="secondary"
              size="icon"
              className="size-12"
              onClick={() => onStampCountChange(Math.max(1, stampCount - 1))}
              disabled={stampCount <= 1}
            >
              <Minus className="size-4" />
            </Button>
            <Input
              type="number"
              min={1}
              value={stampCount}
              onChange={(e) => onStampCountChange(Math.max(1, Number(e.target.value) || 1))}
              onKeyDown={(e) => {
                if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  onStampCountChange(stampCount + 1);
                } else if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  onStampCountChange(Math.max(1, stampCount - 1));
                }
              }}
              className="h-12 w-24 rounded-2xl text-center text-xl font-semibold"
            />
            <Button
              type="button"
              variant="secondary"
              size="icon"
              className="size-12"
              onClick={() => onStampCountChange(stampCount + 1)}
            >
              <Plus className="size-4" />
            </Button>
          </div>
          <p className="text-center text-xs text-muted-foreground">
            Use ↑ ↓ no teclado para ajustar
          </p>
        </div>
      ) : null}
    </div>
  );
}
